import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  Divider,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";

import { Bucket } from "../../../../enum/jobEnums";
import { JobCandidate } from "../../../../api/types/job.types";
import { useGetJobCandidates } from "../../../../api/queries/useGetJobCandidates";
import NoCandidatesFigure from "../../../../assets/images/no-candidates-figure.png";
import { CompareCandidatesModal, useCompareCandidatesModal } from "../../../../components/CompareCandidatesModal";
import {
  AllCandidatesIcon,
  ShortListedIcon,
  QualifiedIcon,
  DisregardedIcon,
} from "../../../../assets/icons/go-hire-page-icons";

import ApplicantRow from "./ApplicantRow";

const CandidatesTab = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.candidatesTab" });
  const { jobId } = useParams<{ jobId: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const bucketType = searchParams.get("candidate-type") || "all-candidates";
  const search = searchParams.get("name") || "";
  const page = Number(searchParams.get("page")) || 1;
  const itemsPerPage = 20;

  const filters = {
    q: search || undefined,
    bucket: bucketType === "all-candidates" ? undefined : (bucketType as Bucket),
    offset: (page - 1) * itemsPerPage,
    limit: itemsPerPage,
  };

  const { data, isLoading } = useGetJobCandidates({ jobId, filters });

  const candidates = data?.data?.items || [];
  const meta = data?.data?.meta;

  const tableColumns = [
    { label: null, value: "checkbox" },
    { label: t("users"), value: "users" },
    { label: t("candidateStatus"), value: "candidateStatus" },
    { label: t("country"), value: "country" },
    { label: t("nationality"), value: "nationality" },
    { label: t("fitScore"), value: "fitScore" },
    { label: t("action"), value: "action" },
  ];

  const MAX_CANDIDATES = 4;
  const [selectedCandidates, setSelectedCandidates] = useState<JobCandidate[]>([]);
  const handleCandidateSelection = (candidate: JobCandidate) => {
    if (selectedCandidates.some((c) => c.id === candidate.id)) {
      setSelectedCandidates((prev: JobCandidate[]) => prev.filter((c: JobCandidate) => c.id !== candidate.id));
    } else if (selectedCandidates.length < MAX_CANDIDATES) {
      setSelectedCandidates((prev: JobCandidate[]) => [...prev, candidate]);
    }
  };

  const isCompareDisabled = selectedCandidates.length < 2;
  const isMaxReached = selectedCandidates.length >= MAX_CANDIDATES;

  const getCompareTooltip = () => {
    if (selectedCandidates.length < 2) return t("compareWarning");
    if (isMaxReached) return t("maxCandidatesReached", { max: MAX_CANDIDATES });
    return "";
  };

  const { openCompareCandidatesModal, handleCloseCompareCandidatesModal, handleOpenCompareCandidatesModal } =
    useCompareCandidatesModal();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newParams = new URLSearchParams(searchParams);
    if (e.target.value) {
      newParams.set("name", e.target.value);
    } else {
      newParams.delete("name");
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", value.toString());
    setSearchParams(newParams);
  };

  return (
    <Box sx={styles.sectionContainer} component="section">
      <Box sx={styles.headerContainer}>
        <Typography sx={styles.title}>{t("candidates")}</Typography>
        <TextField sx={styles.searchInput} placeholder={t("search")} value={search} onChange={handleSearchChange} />
        <Tooltip title={getCompareTooltip()} placement="top-end" arrow>
          <Box>
            <Button variant="primary-sm" disabled={isCompareDisabled} onClick={handleOpenCompareCandidatesModal}>
              {t("compare")}
            </Button>
            <CompareCandidatesModal
              jobId={jobId!}
              open={openCompareCandidatesModal}
              onClose={handleCloseCompareCandidatesModal}
              selectedCandidates={selectedCandidates}
            />
          </Box>
        </Tooltip>
      </Box>
      <Divider flexItem orientation="horizontal" />
      <CandidateType />
      {candidates.length === 0 && !isLoading && <NoCandidatesSection />}

      {isLoading && (
        <Box sx={styles.tableContainerWrapper}>
          <Table sx={styles.tableContainer}>
            <TableHead>
              <TableRow>
                {tableColumns.map(({ label, value }) => {
                  // if (bucketType !== "all-candidates" && value === "candidateStatus") return null;
                  // if (bucketType === "all-candidates" && value === "checkbox") return null;
                  return <TableCell key={value}>{label}</TableCell>;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, idx) => (
                <CandidatesTabSkeletonRow key={idx} bucketType={bucketType} />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {candidates.length > 0 && !isLoading && (
        <>
          <Box sx={styles.tableContainerWrapper}>
            <Table sx={styles.tableContainer}>
              <TableHead>
                <TableRow>
                  {tableColumns.map(({ label, value }) => {
                    // if (bucketType !== "all-candidates" && value === "candidateStatus") return null;
                    // if (bucketType === "all-candidates" && value === "checkbox") return null;
                    return <TableCell key={value}>{label}</TableCell>;
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((applicantData: any, idx: number) => (
                  <ApplicantRow
                    key={idx}
                    applicantData={applicantData}
                    onCandidateSelection={handleCandidateSelection}
                    selectedCandidates={selectedCandidates}
                    isMaxReached={isMaxReached}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <Divider flexItem orientation="horizontal" />

          {!isLoading && data?.data?.meta?.totalPages && data?.data?.meta?.totalPages > 1 && (
            <Pagination
              sx={styles.paginationContainer}
              count={data?.data?.meta?.totalPages}
              page={page}
              onChange={handlePageChange}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CandidatesTab;

const CandidatesTabSkeletonRow = ({ bucketType }: { bucketType: string }) => {
  return (
    <TableRow>
      {bucketType !== "all-candidates" && (
        <TableCell align="left">
          <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: "4px" }} />
        </TableCell>
      )}
      <TableCell align="left" sx={{ width: "100%" }}>
        <Box sx={styles.applicantInfoContainerSkeleton}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </TableCell>
      {bucketType === "all-candidates" && (
        <TableCell align="left">
          <Skeleton variant="rounded" width={80} height={22} />
        </TableCell>
      )}
      <TableCell align="left">
        <Skeleton variant="text" width={80} height={20} />
      </TableCell>
      <TableCell align="left">
        <Skeleton variant="text" width={80} height={20} />
      </TableCell>
      <TableCell align="left">
        <Box sx={styles.applicantFitScoreContainerSkeleton}>
          <Skeleton variant="rounded" width={88} height={8} />
          <Skeleton variant="text" width={30} height={20} />
        </Box>
      </TableCell>
      <TableCell align="left">
        <Box sx={styles.applicantActionContainerSkeleton}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

const NoCandidatesSection = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.overViewTab" });
  return (
    <Box sx={styles.noCandidatesSectionWrapper}>
      <Box component="img" src={NoCandidatesFigure} sx={styles.noCandidatesFigure} />
      <Typography sx={styles.noCandidatesTitle}>{t("noCandidatesFound")}</Typography>
      <Typography sx={styles.noCandidatesDescription}>{t("noCandidatesFoundDescription")}</Typography>
    </Box>
  );
};

const CandidateType = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.candidatesTab" });
  const { t: tBucket } = useTranslation("goHire", { keyPrefix: "enum.bucket" });
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get("candidate-type") || "all-candidates";

  const handleChipClick = (requestType: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("candidate-type", requestType);
    newParams.delete("name");
    newParams.delete("page");
    setSearchParams(newParams);
  };

  const candidateTypes = [
    {
      label: t("allCandidates"),
      value: "all-candidates",
      icon: <AllCandidatesIcon />,
    },
    {
      label: tBucket(Bucket.SHORT_LISTED),
      value: Bucket.SHORT_LISTED,
      icon: <ShortListedIcon />,
    },
    {
      label: tBucket(Bucket.QUALIFIED),
      value: Bucket.QUALIFIED,
      icon: <QualifiedIcon />,
    },
    {
      label: tBucket(Bucket.DISREGARDED),
      value: Bucket.DISREGARDED,
      icon: <DisregardedIcon />,
    },
  ];
  return (
    <Box sx={styles.candidateTypeContainerWrapper}>
      <Box sx={styles.candidateTypeContainer}>
        {candidateTypes.map(({ label, icon, value }) => (
          <Chip
            key={label}
            sx={styles.candidateTypeChip(value === type)}
            onClick={() => handleChipClick(value)}
            label={label}
            icon={icon}
          />
        ))}
      </Box>
    </Box>
  );
};

const styles = {
  sectionContainer: {
    backgroundColor: "#FFF",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    pb: "20px",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 16px 0 16px",
    gap: "16px",
  },
  title: {
    flex: 1,
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  searchInput: {
    width: "240px",
  },
  candidateTypeContainerWrapper: {
    width: "100%",
    pb: { xs: "8px", md: "0" },
    overflowX: "auto",
    px: "16px",
  },
  candidateTypeContainer: {
    width: "fit-content",
    display: "flex",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    borderRadius: "8px",
    overflow: "hidden",
  },
  candidateTypeChip: (isActive: boolean) => ({
    borderRadius: "0",
    border: "none",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",

    borderRight: "1px solid var(--Gray-300, #D0D5DD)",
    background: isActive ? "var(--Gray-50, #F9FAFB)" : "var(--Base-White, #FFF)",
    color: isActive ? "var(--Gray-800, #182230)" : "var(--Gray-700, #344054)",

    gap: "8px",
    padding: "8px 16px 8px 14px",

    "&:last-child": {
      borderRight: "none",
    },

    "& .MuiSvgIcon-root": {
      m: 0,

      "& path": {
        stroke: isActive ? "#182230" : "#667085",
      },
    },

    "& .MuiChip-label": {
      p: 0,
    },
  }),
  paginationContainer: {
    mx: "auto",
    width: "100%",
    padding: { xs: "12px 8px 16px", md: "12px 16px 16px" },
    "& .MuiPagination-ul": {
      justifyContent: "center",
    },
  },
  tableContainerWrapper: {
    width: "100%",
    overflowX: "auto",
  },
  tableContainer: {
    "& .MuiTableHead-root": {
      backgroundColor: "var(--Gray-50, #F9FAFB)",
    },
    "& .MuiTableCell-head": {
      borderTop: "1px solid var(--Gray-200, #EAECF0)",
      color: "var(--Gray-600, #475467)",
      fontFamily: "Montserrat",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      padding: "12px 24px",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      padding: "12px 18px",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  noCandidatesSectionWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    padding: "48px 24px",
  },
  noCandidatesFigure: {
    width: "240px",
    height: "180px",
  },
  noCandidatesTitle: {
    color: "#111",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "32px",
  },
  noCandidatesDescription: {
    color: "#5E6278",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "24px",
  },
  applicantInfoContainerSkeleton: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  applicantFitScoreContainerSkeleton: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  applicantActionContainerSkeleton: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
};
